import * as React from 'react';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector, batch } from 'react-redux';
import { loginRequest, loginSuccess, setAuthErrorMessage, loginFailure, updateAvailable } from '../../actions/auth';
import { generateSalesforceOAuthUrl, handleLoginBrowser, handleLoginDesktop } from '../../utils/auth';
import { retrieveDataForAppInitialization } from '../../utils/init';
import { changeAlertText, setInitializationError } from '../../actions/index';
import officeApi from '../../../api/officeApi';
import { ChoiceGroup } from '@fluentui/react';
import { validateClientVersion } from '../../../api/emailsApi/client';
import { StatusBarAlert } from '../statusBar/StatusBarAlert';

import './Login.scss';

export const Login = () => {
  const dispatch = useDispatch();
  const [disabled, setDisabled] = useState(true);
  const [isSandbox, setIsSandbox] = useState(false);
  const requestingLogin = useSelector((state) => state.auth.requestingLogin);
  const onLoginError = useSelector((state) => state.auth.error);
  const authenticationFailure = useSelector((state) => state.auth.authenticationFailure);
  const errorMessage = onLoginError ? 'Login was unsuccessful' : 'Unexpected Error Occurred';

  useEffect(() => {
    if (localStorage.getItem('upgraded')) {
      dispatch(changeAlertText(`Docrio Email has been upgraded to ${process.env.VERSION}`));
      localStorage.removeItem('upgraded');
    }
  }, []);

  const dispatchError = (error) => {
    batch(() => {
      dispatch(loginRequest(false));
      dispatch(changeAlertText(error, 'error'));
    });
  };

  const onLoginClicked = async () => {
    dispatch(loginRequest(true));
    localStorage.clear();
    try {
      await initiateOAuth();
    } catch (e) {
      if (e.message === 'invalid client version') {
        dispatch(updateAvailable());
        return;
      }
      // handle the case where the userid cannot be retrieved
      dispatch(loginFailure(e.message));
      dispatchError(e.message);
      return;
    }
    await confirmAndRetrieveUserData();
  };

  const initiateOAuth = async () => {
    const [salesforceAuthUrl, validClientVersion] = await Promise.all([generateSalesforceOAuthUrl(isSandbox), validateClientVersion()]);
    if (!validClientVersion) {
      throw new Error('invalid client version');
    }
    officeApi.getPlatform() === 'OfficeOnline' ? await handleLoginBrowser(salesforceAuthUrl) : await handleLoginDesktop(salesforceAuthUrl);
  };

  const confirmAndRetrieveUserData = async () => {
    try {
      await retrieveDataForAppInitialization(dispatch);
    } catch (err) {
      console.log(err);
      if (authenticationFailure) {
        dispatch(loginFailure(err.message));
        dispatchError(errorMessage);
      }
      return;
    }

    batch(() => {
      dispatch(loginSuccess());
      dispatch(changeAlertText('Success. Welcome to Docrio Email!'));
    });
  };

  const [heading, paragraph, button] = requestingLogin ?
    [
      'Connecting to Litify...',
      'Please follow the instructions on the authorization screen to continue logging into your account.',
      '',
    ] :
    [
      'Welcome to Docrio Email',
      'In order to configure the right experience, please select which environment you are logging into. By continuing, you are allowing access to your data.',
      (<button id='login-button' key='login-button' className='login-button' disabled={disabled} onClick={onLoginClicked}>Log In to Docrio Email</button>),
    ];

  const choiceGroupStyles = {
    root: { fontSize: '12px', fontWeight: '600', borderColor: '#000' },
  };

  const options = [
    { key: 'Production', text: 'Production', styles: choiceGroupStyles },
    { key: 'Sandbox', text: 'Sandbox', styles: choiceGroupStyles },
  ];

  const handleRadioSelect = (option) => {
    batch(() => {
      dispatch(setAuthErrorMessage(''));
      dispatch(setInitializationError(false));
    });
    setIsSandbox(option.currentTarget.id.includes('Sandbox'));
    if (!disabled) {
      return;
    }

    setDisabled(!disabled);
  };

  return (
    <>
      <StatusBarAlert
        text='Once logged in, any events that did not sync while you were logged out will sync.'
        status='info'
      />
      <div className='login-container'>
        <div className='login-content'>
          <h4 className='login-heading'>{heading}</h4>
          <p className='login-paragraph'>{paragraph}</p>
          { !requestingLogin && <ChoiceGroup options={options} onChange={handleRadioSelect} required={true} /> }
          {button}
        </div>
      </div>
    </>
  );
};
